
import React, { useMemo, useState } from 'react';
import { Empresa, ClientePerfil } from '../types';

interface MenuHeaderProps {
  empresa: Empresa;
  customer?: ClientePerfil | null;
  onOpenLogin: () => void;
  onLogout: () => void;
}

const DIAS_SEMANA = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];
const DAYS_FOR_INDEX = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

export const MenuHeader: React.FC<MenuHeaderProps> = ({ empresa, customer, onOpenLogin, onLogout }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const parsedHorarios = useMemo(() => {
    if (!empresa.horarios) return {};
    try { 
      const h = typeof empresa.horarios === 'string' ? JSON.parse(empresa.horarios) : empresa.horarios;
      return h || {};
    } catch { return {}; }
  }, [empresa.horarios]);

  const todayName = useMemo(() => DAYS_FOR_INDEX[new Date().getDay()], []);

  const isOpen = useMemo(() => {
    if (!empresa.horarios) return true;
    try {
      const h = parsedHorarios;
      const diaHoje = todayName;
      const config = h[diaHoje];
      
      if (!config || !config.abertura || !config.fechamento) return false;
      
      const toMin = (s: string) => {
        const [hh, mm] = s.split(':').map(Number);
        return hh * 60 + (mm || 0);
      };
      
      const now = new Date();
      const atual = now.getHours() * 60 + now.getMinutes();
      const ab = toMin(config.abertura);
      const fe = toMin(config.fechamento);
      
      if (fe < ab) return atual >= ab || atual <= fe;
      return atual >= ab && atual <= fe;
    } catch { return true; }
  }, [parsedHorarios, todayName]);

  const todayHours = useMemo(() => {
    const config = parsedHorarios[todayName];
    return (config && config.abertura && config.fechamento) 
      ? `${config.abertura} - ${config.fechamento}` 
      : 'Fechado';
  }, [parsedHorarios, todayName]);

  return (
    <div className="bg-slate-50 sticky top-0 z-50 border-b border-slate-100/50 pb-6 transition-all">
      <div className="max-w-xl mx-auto px-4 pt-4">
        
        {/* Perfil do Cliente / Login */}
        <div className="flex justify-end mb-4">
          {customer ? (
            <div className="flex items-center gap-3 bg-white border border-slate-100 p-1.5 pr-4 rounded-full shadow-sm">
              <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center text-white font-black text-[10px] uppercase">
                {customer.nome.charAt(0)}
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] font-black text-slate-900 leading-none">Olá, {customer.nome.split(' ')[0]}</span>
                <button onClick={onLogout} className="text-[9px] font-bold text-orange-600 uppercase tracking-widest text-left mt-0.5 hover:underline">Sair</button>
              </div>
            </div>
          ) : (
            <button onClick={onOpenLogin} className="flex items-center gap-2 bg-slate-900 text-white px-4 py-2 rounded-full shadow-lg hover:bg-orange-600 transition-all">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
              <span className="text-[10px] font-black uppercase tracking-widest">Entrar</span>
            </button>
          )}
        </div>

        {/* CAIXA PRINCIPAL DE IDENTIDADE */}
        <div className="relative bg-white p-6 rounded-[2.5rem] border border-slate-100 shadow-sm mb-2">
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 rounded-[1.25rem] bg-slate-50 shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden flex items-center justify-center font-black text-slate-200 text-2xl shrink-0">
              {empresa.logo_url ? <img src={empresa.logo_url} className="w-full h-full object-cover" /> : empresa.nome.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-black text-slate-900 tracking-tight truncate leading-tight">{empresa.nome}</h1>
              {empresa.descricao && (
                <p className="text-[11px] font-medium text-slate-500 leading-snug mt-1 line-clamp-1">
                  {empresa.descricao}
                </p>
              )}
            </div>
          </div>

          {/* STATUS FLUTUANTE ANCORADO E SOBREPOSTO */}
          <div className="absolute left-1/2 -translate-x-1/2 bottom-0 translate-y-1/2 flex items-center gap-2 bg-white border border-slate-100 shadow-lg shadow-slate-200/50 rounded-full px-3 py-1.5 z-10 whitespace-nowrap transition-all border-b-2">
            <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${isOpen ? 'bg-emerald-50 border-emerald-100 text-emerald-600' : 'bg-red-50 border-red-100 text-red-600'}`}>
              <div className={`w-1 h-1 rounded-full ${isOpen ? 'bg-emerald-500' : 'bg-red-500'}`}></div>
              <span className="text-[8px] font-black uppercase tracking-widest">{isOpen ? 'Aberto' : 'Fechado'}</span>
            </div>
            <span className="text-[9px] font-bold text-slate-400 pr-1">{todayHours}</span>
          </div>
        </div>

        {/* ABA DE MAIS INFORMAÇÕES */}
        <div className="mt-6 flex justify-center">
          {isExpanded && (
            <div className="w-full mt-2 p-5 bg-white rounded-2xl border border-slate-100 space-y-4 animate-fade-in shadow-sm">
              <h4 className="text-[10px] font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
                <svg className="w-3.5 h-3.5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Horários de Funcionamento
              </h4>
              <div className="space-y-2">
                {DAYS_FOR_INDEX.map((dia) => {
                  const config = parsedHorarios[dia];
                  const isToday = dia === todayName;
                  return (
                    <div key={dia} className={`flex justify-between items-center text-[10px] font-bold ${isToday ? 'text-orange-600' : 'text-slate-400'}`}>
                      <span className="uppercase tracking-widest">{dia}</span>
                      <span>{config && config.abertura && config.fechamento ? `${config.abertura} - ${config.fechamento}` : 'Fechado'}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <button onClick={() => setIsExpanded(!isExpanded)} className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1 hover:text-orange-600 transition-colors">
            {isExpanded ? 'Ocultar detalhes' : 'Ver horários completos'}
            <svg className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};
